import { Schema } from 'effect';
import {
  LIVE_HTML_BLOCK_ID_PATTERN,
  LIVE_HTML_CONTRACT_VERSION,
  type LiveHtmlEvent,
  type LiveHtmlSessionResource,
} from './live-html-contract.js';
import { withParseApi } from './schema-codec.js';

export * from './live-html-contract.js';

const blockIdRegex = new RegExp(LIVE_HTML_BLOCK_ID_PATTERN);
const seqSchema = Schema.Int.check(Schema.isGreaterThan(0));
const blockIdSchema = Schema.String.check(Schema.isPattern(blockIdRegex));
const htmlBlockSchema = Schema.Trim.check(Schema.isMinLength(1));
const targetSchema = Schema.String.check(
  Schema.makeFilter((value: string) =>
    value.startsWith('#') && blockIdRegex.test(value.slice(1)),
  ),
);
const viewerUrlSchema = Schema.String.check(
  Schema.makeFilter((value: string) => /^\/s\/[^/]+$/.test(value)),
);
const eventsUrlSchema = Schema.String.check(
  Schema.makeFilter((value: string) => /^\/s\/[^/]+\/events$/.test(value)),
);

const eventBase = {
  version: Schema.Literal(LIVE_HTML_CONTRACT_VERSION),
  seq: seqSchema,
};

const liveHtmlEventEnvelopeSchema = Schema.Union([
  Schema.Struct({
    ...eventBase,
    type: Schema.Literal('progress'),
    phase: Schema.Trim.check(Schema.isMinLength(1)),
  }),
  Schema.Struct({
    ...eventBase,
    type: Schema.Literal('append'),
    block_id: blockIdSchema,
    html: htmlBlockSchema,
  }),
  Schema.Struct({
    ...eventBase,
    type: Schema.Literal('replace'),
    target: targetSchema,
    html: htmlBlockSchema,
  }),
  Schema.Struct({
    ...eventBase,
    type: Schema.Literal('done'),
  }),
  Schema.Struct({
    ...eventBase,
    type: Schema.Literal('error'),
    message: Schema.Trim.check(Schema.isMinLength(1)),
  }),
]);

const liveHtmlEventCodec = liveHtmlEventEnvelopeSchema.check(
  Schema.makeFilter((event, _ast, _options) => {
    if (event.type !== 'append' && event.type !== 'replace') return undefined;
    const rootId = event.html.trim().match(
      /^<[a-zA-Z][\w-]*\b[^>]*\bid\s*=\s*["']([^"']+)["']/,
    )?.[1];
    const expectedId = event.type === 'append' ? event.block_id : event.target.slice(1);
    if (rootId !== expectedId) {
      return { path: ['html'], issue: 'HTML root id must match the event address' };
    }
    return undefined;
  }),
);

export const liveHtmlEventSchema = withParseApi(liveHtmlEventCodec, {
  onExcessProperty: 'error',
}) as typeof liveHtmlEventCodec & {
  parse: (input: unknown) => LiveHtmlEvent;
  safeParse: (input: unknown) =>
    | { readonly success: true; readonly data: LiveHtmlEvent }
    | { readonly success: false; readonly error: unknown };
};

const liveHtmlSessionResourceCodec = Schema.Struct({
  object: Schema.Literal('live_html_session'),
  version: Schema.Literal(LIVE_HTML_CONTRACT_VERSION),
  id: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128)),
  status: Schema.Literal('streaming'),
  url: viewerUrlSchema,
  events_url: eventsUrlSchema,
}).check(
  Schema.makeFilter((resource) => {
    if (resource.url !== `/s/${resource.id}`) {
      return { path: ['url'], issue: 'viewer URL must address the returned session id' };
    }
    if (resource.events_url !== `${resource.url}/events`) {
      return { path: ['events_url'], issue: "events URL must be the viewer URL's /events endpoint" };
    }
    return undefined;
  }),
);

export const liveHtmlSessionResourceSchema = withParseApi(liveHtmlSessionResourceCodec, {
  onExcessProperty: 'error',
}) as typeof liveHtmlSessionResourceCodec & {
  parse: (input: unknown) => LiveHtmlSessionResource;
  safeParse: (input: unknown) =>
    | { readonly success: true; readonly data: LiveHtmlSessionResource }
    | { readonly success: false; readonly error: unknown };
};
